import {Dialog,DialogTitle,DialogContent,DialogActions,TextField,Button,IconButton,Box} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import i18n from "@i18n/i18n";

interface AddModelTypeDialogProps {
  open: boolean;
  inputs: string[];
  onInputChange: (index: number, value: string) => void;
  onAddInput: () => void;
  onRemoveInput: (index: number) => void;
  onClose: () => void;
  onSave: () => void;
  isSaving: boolean;
  duplicateErrors: Set<number>;
  selectedRowId: string | number | null;
  getExistingChildEntries: (parentId: string | number) => string[];
}

const AddModelTypeDialog = ({
  open,
  inputs,
  onInputChange,
  onAddInput,
  onRemoveInput,
  onClose,
  onSave,
  isSaving,
  duplicateErrors,
  selectedRowId,
  getExistingChildEntries,
}: AddModelTypeDialogProps) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {i18n.t("rebar-material-grid.add-model-type-dialog.title")}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {inputs.map((input, index) => {
            const hasError = duplicateErrors.has(index);
            const existingEntries = selectedRowId
              ? getExistingChildEntries(selectedRowId)
              : [];
            const trimmedInput = input.toLowerCase().trim();
            const isDuplicateOfExisting =
              trimmedInput !== "" && existingEntries.includes(trimmedInput);

            return (
              <Box
                key={index}
                sx={{ display: "flex", gap: 1, alignItems: "center" }}
              >
                <TextField
                  autoFocus={index === 0}
                  label={i18n.t(
                    "rebar-material-grid.add-model-type-dialog.input.label",
                    { number: index + 1 }
                  )}
                  fullWidth
                  variant="outlined"
                  value={input}
                  onChange={(e) => onInputChange(index, e.target.value)}
                  placeholder={i18n.t(
                    "rebar-material-grid.add-model-type-dialog.input.placeholder",
                    { number: index + 1 }
                  )}
                  disabled={isSaving}
                  error={hasError}
                  helperText={
                    hasError
                      ? isDuplicateOfExisting
                        ? i18n.t(
                            "rebar-material-grid.add-model-type-dialog.validation.already-exists"
                          )
                        : i18n.t(
                            "rebar-material-grid.add-model-type-dialog.validation.duplicate-in-list"
                          )
                      : ""
                  }
                />
                {inputs.length > 1 && (
                  <IconButton
                    onClick={() => onRemoveInput(index)}
                    size="small"
                    disabled={isSaving}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            );
          })}
          <Button
            startIcon={<AddIcon />}
            onClick={onAddInput}
            variant="outlined"
            size="small"
            sx={{ alignSelf: "flex-start", mt: 1 }}
            disabled={isSaving}
          >
            {i18n.t(
              "rebar-material-grid.add-model-type-dialog.add-another-button"
            )}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={isSaving}>
          {i18n.t("rebar-material-grid.add-model-type-dialog.cancel-button")}
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          type="submit"
          disabled={
            !inputs.some((val) => val.trim() !== "") ||
            duplicateErrors.size > 0 ||
            isSaving
          }
        >
          {isSaving
            ? i18n.t("rebar-material-grid.add-model-type-dialog.adding-button")
            : i18n.t("rebar-material-grid.add-model-type-dialog.add-button")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddModelTypeDialog;
